import { useEffect, useState } from "react";
import { Row, Spacer } from "../../../components/Layout/Layout";
import {
  deleteEmergencyCenterSetting,
  editEmergencyCenterSetting,
  getEmergencyCenterSettings,
} from "../../../core/api/responsecenters/responsecenters";
import { EmergencyCenterSetting } from "../../../core/api/responsecenters/types";
import useTranslations from "../../../core/i18n/useTranslations";
import useUser from "../../../core/user/useUser";
import Button from "../../../ui-lib/components/Button/Button";
import Table from "../../../ui-lib/components/Tables/Table";
import TableCell from "../../../ui-lib/components/Tables/TableCell";
import { EditEmergencyCenterSetting } from "./EditEmergencyCenterSetting";
import { notifyApiErrors } from "../../../core/helpers/helpers";

const AlarmReceptionSettings = ({ id }: { id: number }) => {
  const t = useTranslations();
  const { authenticatedRequest } = useUser();
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isEditSettingOpen, setIsEditSettingOpen] = useState(false);
  const [settings, setSettings] = useState<EmergencyCenterSetting[]>([]);
  const [selectedSetting, setSelectedSetting] =
    useState<EmergencyCenterSetting>();
  const fetchSettings = async () => {
    setIsLoadingSettings(true);
    try {
      const result = await getEmergencyCenterSettings(id, authenticatedRequest);
      setSettings(result.data);
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const editSetting = async (setting: EmergencyCenterSetting) => {
    setIsLoadingSettings(true);
    try {
      await editEmergencyCenterSetting(id, setting, authenticatedRequest);
      await fetchSettings();
    } catch (error: any) {
      setIsLoadingSettings(false);
      notifyApiErrors(error.response?.data?.errors);
    }
  };

  const deleteSetting = async (settingId: string | number) => {
    setIsLoadingSettings(true);
    try {
      await deleteEmergencyCenterSetting(id, settingId, authenticatedRequest);
      await fetchSettings();
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
    } finally {
      setIsLoadingSettings(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [id]);

  return (
    <>
      <Row type="space" align="center">
        <h2>
          {t("AlarmReceptions:advanced_settings_emergencycentersettings_label")}
        </h2>
        {isEditSettingOpen && (
          <EditEmergencyCenterSetting
            settingsData={selectedSetting}
            isOpen={isEditSettingOpen}
            editSetting={editSetting}
            closeModal={() => {
              setSelectedSetting(undefined);
              setIsEditSettingOpen(false);
            }}
            saveButtonTitle={t("AlarmReceptions:save_setting")}
          />
        )}
        <Button
          variant="secondary"
          text={t("AlarmReceptions:add_new_setting")}
          onClick={() => {
            setIsEditSettingOpen(true);
          }}
        />
      </Row>
      <Spacer size={16} />
      <Table<EmergencyCenterSetting>
        columns={[
          {
            header: t(
              "AlarmReceptions:advanced_settings_emergencycentersettings_setting"
            ),
            fieldTemplate: (rowData) => <TableCell value={rowData.setting} />,
          },
          {
            header: t(
              "AlarmReceptions:advanced_settings_emergencycentersettings_value"
            ),
            fieldTemplate: (rowData) => <TableCell value={rowData.value} />,
          },
        ]}
        items={settings}
        rowActions={[
          {
            icon: "pencil-alt",
            text: t("Common:label_edit"),
            onClick: (rowData) => {
              setSelectedSetting(rowData);
              setIsEditSettingOpen(true);
            },
          },
          {
            icon: "trash",
            text: t("Common:delete"),
            iconVariant: "secondary",
            onClick: (rowData) => deleteSetting(rowData.id ?? -1),
          },
        ]}
        hideEmptyMessage
        noRowsMessage={t(
          "AlarmReceptions:advanced_settings_emergencycentersettings_nosettings"
        )}
        showRowsActionsOnHover
        isLoading={isLoadingSettings}
        showRowHover
        withShowMore
      />
    </>
  );
};
export default AlarmReceptionSettings;
